import { useState } from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  DialogClose,
  DialogDescription,
  DialogTitle,
} from "common/components/ui/Dialog";
import {
  DrawerClose,
  DrawerDescription,
  DrawerTitle,
} from "common/components/ui/Drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FormField from "common/components/ui/FormField";
import SelectButton from "common/components/ui/SelectButton";
import useCrmSources from "common/datahooks/useCrmSources";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type {
  IntegrationCrmType,
  LeadsSearchParams,
  SelectedFacetParam,
} from "common/types";

import { INTEGRATIONS_LIST } from "../../../../../integrations/constants";
import useLeadExports from "../../../../datahooks/useLeadExports";
import ExportSummary from "./ExportSummary";

import type { OUTREACH_CRM_EXPORT_OPTIONS } from "../../../../constants";
import type { CrmExportSummary } from "../../../../types";

interface ListAndCampaignSelectProps {
  crmType: IntegrationCrmType;
  type: keyof typeof OUTREACH_CRM_EXPORT_OPTIONS;
  onBack: () => void;
  searchParams: LeadsSearchParams<SelectedFacetParam>;
}

export default function ListAndCampaignSelect({
  crmType,
  type,
  onBack,
  searchParams,
}: ListAndCampaignSelectProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [inputValue, setInputValue] = useState("");
  const [summary, setSummary] = useState<CrmExportSummary>();
  const [selectedOption, setSelectedOption] = useState<{
    id: string;
    name: string;
  }>();

  const { exportLeadsToOutreachCrm, isExportingLeadsToOutreachCrm } =
    useLeadExports();

  const { crmSources, iseLoadingCrmSources } = useCrmSources({
    crmType,
    sourceType: `${type}s`,
  });

  const { title } = INTEGRATIONS_LIST.find(
    ({ title }) => title.toLowerCase() === crmType,
  );

  const label = `${title} ${type}`;

  const filteredOptions = crmSources
    ? crmSources.filter(({ name }) =>
        name.toLowerCase().includes(inputValue.toLowerCase()),
      )
    : [];

  function onExport() {
    exportLeadsToOutreachCrm({
      crmType,
      searchParams,
      type,
      id: selectedOption.id,
    }).then((exportSummary) => setSummary(exportSummary));
  }

  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;
  const Close = isDesktop ? DialogClose : DrawerClose;

  if (summary)
    return (
      <>
        <Title>Export summary</Title>
        <Description>A detailed breakdown of your data export</Description>

        <ExportSummary summary={summary} crmType={crmType} />

        <Close asChild>
          <Button variant="secondary-black" className="ml-auto">
            Close
          </Button>
        </Close>
      </>
    );

  return (
    <>
      <Title className="mb-8">Select {label}</Title>

      <FormField
        label={`Select an ${label}`}
        htmlFor={`${crmType}-${type}-select`}
        hint={`Your leads will be exported to the selected ${type}`}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SelectButton
              id={`${crmType}-${type}-select`}
              hasSelection={!!selectedOption}
              isLoading={iseLoadingCrmSources}
            >
              {selectedOption ? selectedOption.name : `Select a ${type}`}
            </SelectButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="z-aboveDialog w-[--radix-popper-anchor-width]">
            <RenderIf condition={crmSources?.length > 5}>
              <MenuSearchInput
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
            </RenderIf>

            <DropdownMenuList>
              {filteredOptions.length ? (
                filteredOptions.map((option) => {
                  const { id, name } = option;

                  return (
                    <DropdownMenuItem
                      key={id}
                      onSelect={() => setSelectedOption(option)}
                    >
                      {name}
                    </DropdownMenuItem>
                  );
                })
              ) : (
                <p className="mx-4 break-words py-4 text-center text-black-500">
                  No results for{" "}
                  <span className="text-black-950">{inputValue}</span>
                </p>
              )}
            </DropdownMenuList>
          </DropdownMenuContent>
        </DropdownMenu>
      </FormField>

      <div className="flex items-center justify-between gap-x-2.5 mt-8">
        {crmType === "instantly" && (
          <Button variant="secondary-black" onClick={onBack}>
            Back
          </Button>
        )}

        <Button
          className="ml-auto"
          onClick={onExport}
          disabled={!selectedOption}
          isLoading={isExportingLeadsToOutreachCrm}
        >
          Next
        </Button>
      </div>
    </>
  );
}
